import { useState } from "react";
import { motion } from "framer-motion";
import { ProjectsPanel } from "../../components/ProjectsPanel";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Progress } from "../../components/ui/progress";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../components/ui/tooltip";
import {
  DollarSign,
  TrendingUp,
  Target,
  BarChart3,
  Zap,
  Shield,
  AlertTriangle,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Calendar,
  Building2,
  Info,
  X,
  ExternalLink,
  Loader2,
  AlertCircle,
  Database,
  Play,
  CheckCircle2,
  FileText,
  MapPin,
  Clock,
  TrendingDown,
  Lightbulb,
  Bookmark,
  BookmarkCheck,
  Users,
  Swords,
} from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "../../components/ui/alert";
import { NEAR_MISS_ALERTS } from "../../data/mockData";
import { useCharities, useGrantMatches, useCharityMeasures, useDefaultSearchMatches, useDirectCompetitors, useCompetitionSummary, useGrantCompetition } from "../../hooks/useFundMatch";
import { useProjectMatches, useProjectMeasures, useDiscoverProject } from "../../hooks/useProjects";

const measureIcons = {
  opportunities: DollarSign,
  expectations: TrendingUp,
  survey: Target,
  outlook: BarChart3,
  leverage: Zap,
  positioning: BarChart3,
  efficiency: Zap,
  assurance: Shield,
};

const measureLabels = {
  opportunities: "Opportunities",
  expectations: "Expectations",
  survey: "Survey $/entity",
  outlook: "Outlook",
  leverage: "Leverage",
  positioning: "Positioning",
  efficiency: "Efficiency",
  assurance: "Assurance",
};

const measureDescriptions = {
  opportunities: "Total qualified funding available",
  expectations: "Probability-weighted expected value",
  survey: "Competition density ($/entity)",
  outlook: "YoY funding analysis",
  leverage: "L1-L4 goal alignment score",
  positioning: "Rank vs historical recipients",
  efficiency: "Expected $ per effort hour",
  assurance: "Funder reliability × expected amount",
};

const formatMeasureValue = (key, value) => {
  if (value === null || value === undefined) return null;
  const num = typeof value === "number" ? value : parseFloat(value);
  if (isNaN(num)) return "—";
  switch (key) {
    case "opportunities":
    case "expectations":
    case "survey":
    case "assurance":
      return num > 1_000_000 ? `$${(num / 1_000_000).toFixed(1)}M` : `$${(num / 1000).toFixed(0)}K`;
    case "outlook":
      return `+${num}%`;
    case "leverage":
      return num.toFixed(2);
    case "positioning":
      return `${num}%ile`;
    case "efficiency":
      return `$${num.toLocaleString()}`;
    default:
      return value;
  }
};

// ── v5: ConfidenceBadge ───────────────────────────────────────────────────────
const CONFIDENCE_CONFIG = {
  HIGH:             { label: "High confidence",       color: "bg-green-100 text-green-700 border-green-200" },
  LIMITED_EVIDENCE: { label: "Limited evidence",      color: "bg-amber-100 text-amber-700 border-amber-200" },
  VECTOR_ONLY:      { label: "Vector only",           color: "bg-zinc-100 text-zinc-500 border-zinc-200" },
};

const ConfidenceBadge = ({ confidence }) => {
  if (!confidence) return null;
  const cfg = CONFIDENCE_CONFIG[confidence] || CONFIDENCE_CONFIG.VECTOR_ONLY;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded border ${cfg.color}`}>
      <Info className="h-2.5 w-2.5" />
      {cfg.label}
    </span>
  );
};

// ── v5: MeasureCard with confidence badge ─────────────────────────────────────
const MeasureCard = ({ label, value, icon, iconBg, confidence }) => (
  <Card>
    <CardContent className="p-5">
      <div className="flex items-center justify-between mb-1">
        <p className="text-sm text-zinc-500">{label}</p>
        <div className={`w-10 h-10 rounded-xl ${iconBg?.startsWith("from") ? `bg-gradient-to-br ${iconBg}` : iconBg} flex items-center justify-center`}>
          {icon}
        </div>
      </div>
      {value === null || value === undefined ? (
        <div className="mb-1">
          <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
            <Loader2 className="h-3 w-3" />
            Pending Analysis
          </span>
        </div>
      ) : (
        <p className="text-2xl font-heading font-bold text-zinc-900 mb-1">{value}</p>
      )}
      <ConfidenceBadge confidence={confidence} />
    </CardContent>
  </Card>
);

// Source → jurisdiction label/badge
const SOURCE_BADGE = {
  GRANTS_GOV_US: { label: "🇺🇸 Federal", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  grant_programs: { label: "🇨🇦 Provincial", cls: "bg-red-50 text-red-700 border-red-200" },
  funder_programs: { label: "🌐 Foundation", cls: "bg-purple-50 text-purple-700 border-purple-200" },
};

// ── Expandable step row for Next Steps accordion ──────────────────────────────
const ExpandableStep = ({ step }) => {
  const [open, setOpen] = useState(false);
  const StepIcon = step.icon;
  return (
    <div className="border-b border-zinc-100 last:border-0">
      <button
        className="w-full flex items-center gap-2.5 px-3 py-2.5 hover:bg-zinc-50 transition-colors text-left"
        onClick={e => { e.stopPropagation(); setOpen(v => !v); }}
      >
        <StepIcon className={`h-4 w-4 shrink-0 ${step.color}`} />
        <span className="flex-1 text-xs font-medium text-zinc-800">{step.label}</span>
        <ChevronDown className={`h-3 w-3 text-zinc-400 transition-transform shrink-0 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-2" onClick={e => e.stopPropagation()}>
          <p className="text-xs text-zinc-600 leading-relaxed">{step.desc}</p>
          {step.action && (
            <button
              onClick={e => { e.stopPropagation(); step.action(); }}
              className="text-xs text-teal-600 hover:text-teal-700 hover:underline flex items-center gap-1"
            >
              {step.actionLabel} <ExternalLink className="h-3 w-3" />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const GrantCard = ({ grant, onNavigate, onSave, isSaved, orgBN }) => {
  const [showExplanation, setShowExplanation] = useState(false);
  const [selectedMeasure, setSelectedMeasure] = useState(null);
  const Icon = grant.status === "near-miss" ? AlertTriangle : Target;
  const statusColor = grant.status === "near-miss" ? "amber" : "teal";
  const rp = grant.retrieval_path;   // v5 F1.7 explanation data
  const rationale = grant.rationale; // v8 GraphRAG rationale
  const srcBadge = SOURCE_BADGE[grant.source] || null;

  const { data: grantCompetition } = useGrantCompetition(orgBN, grant.id, showExplanation && !!orgBN && !!grant.id);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl border border-zinc-200 p-5 hover:border-teal-200 hover:shadow-md transition-all cursor-pointer"
      data-testid={`grant-card-${grant.id}`}
      onClick={() => setShowExplanation(v => !v)}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-heading font-semibold text-zinc-900">{grant.programName}</h3>
            {grant.status === "near-miss" && (
              <Badge className="bg-amber-100 text-amber-700 text-xs">Near-miss</Badge>
            )}
            {srcBadge && (
              <span className={`text-[10px] px-1.5 py-0.5 rounded border font-medium ${srcBadge.cls}`}>{srcBadge.label}</span>
            )}
          </div>
          <p className="text-sm text-zinc-500">{grant.funderName}</p>
        </div>
        <div className="text-right">
          <div className="flex items-center gap-1 justify-end">
            <span className={`text-2xl font-heading font-bold text-${statusColor}-600`}>
              {grant.matchScore}%
            </span>
          </div>
          <p className="text-xs text-zinc-500">match score</p>
          {grantCompetition?.mirror_plus_direct_competitors > 0 && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex items-center gap-0.5 text-[10px] text-orange-600 font-medium cursor-default mt-0.5">
                    <Swords className="h-2.5 w-2.5" />
                    {grantCompetition.mirror_plus_direct_competitors} competing
                  </span>
                </TooltipTrigger>
                <TooltipContent side="left" className="max-w-xs">
                  <p className="text-xs">{grantCompetition.mirror_plus_direct_competitors} direct or mirror competitors are also eligible for this grant based on mission overlap.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      </div>

      {/* Amount & Deadline */}
      <div className="flex items-center gap-4 mb-4 text-sm">
        <div className="flex items-center gap-1 text-zinc-600">
          <DollarSign className="h-4 w-4" />
          <span className="font-medium">{grant.amount && grant.amount !== 'Varies' ? grant.amount : '—'}</span>
        </div>
        <div className="flex items-center gap-1 text-zinc-600">
          <Calendar className="h-4 w-4" />
          <span>{grant.deadline && grant.deadline !== 'Rolling' && !isNaN(Date.parse(grant.deadline))
            ? new Date(grant.deadline).toLocaleDateString("en-CA", { month: "short", day: "numeric" })
            : (grant.deadline || 'Rolling')}</span>
        </div>
        <Badge variant="outline" className="text-xs">{grant.sector}</Badge>
      </div>

      {/* Measures Grid — click any tile for details (highlighted in details panel below) */}
      <div className="grid grid-cols-4 gap-2 mb-2">
        {Object.entries(grant.measures).map(([key, value]) => {
          const MeasureIcon = measureIcons[key];
          const isSelected = selectedMeasure === key;
          return (
            <div
              key={key}
              className={`rounded-lg p-2 text-center cursor-pointer transition-all border relative group ${
                isSelected
                  ? 'bg-teal-50 border-teal-300 shadow-sm'
                  : 'bg-zinc-50 border-transparent hover:bg-teal-50 hover:border-teal-200'
              }`}
              onClick={e => {
                e.stopPropagation();
                setSelectedMeasure(isSelected ? null : key);
                setShowExplanation(true);
              }}
              title={measureDescriptions[key]}
            >
              <MeasureIcon className={`h-3 w-3 mx-auto mb-1 ${isSelected ? 'text-teal-500' : 'text-zinc-400'}`} />
              {formatMeasureValue(key, value) === null ? (
                <p className="text-[10px] font-medium text-amber-600">Pending</p>
              ) : (
                <p className="text-xs font-bold text-zinc-900">{formatMeasureValue(key, value)}</p>
              )}
              <p className="text-[10px] text-zinc-500 truncate">{measureLabels[key]}</p>
              <span className="absolute top-0.5 right-0.5 text-[8px] text-zinc-300 group-hover:text-teal-400">ⓘ</span>
            </div>
          );
        })}
      </div>

      {/* Confidence Interval — only show when backend provides data */}
      <div className="flex items-center justify-between text-xs">
        {grant.confidenceInterval ? (
          <div className="flex items-center gap-2">
            <span className="text-zinc-500">Confidence:</span>
            <div className="flex items-center gap-1">
              <span className="text-zinc-400">{((grant.confidenceInterval.low || 0) * 100).toFixed(0)}%</span>
              <div className="w-16 h-1.5 bg-zinc-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-slate-400 to-teal-500 rounded-full"
                  style={{ width: `${grant.matchScore}%` }}
                />
              </div>
              <span className="text-zinc-400">{((grant.confidenceInterval.high || 0) * 100).toFixed(0)}%</span>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <span className="text-zinc-500">Confidence:</span>
            <span className="text-zinc-400">{grant.matchScore >= 65 ? 'High' : grant.matchScore >= 55 ? 'Moderate' : 'Low'}</span>
          </div>
        )}
        <div className="flex items-center gap-2">
          {grant.applicationUrl && (
            <Button
              variant="ghost" size="sm"
              className="text-zinc-400 hover:text-zinc-600 p-0 h-auto text-[11px]"
              onClick={(e) => { e.stopPropagation(); window.open(grant.applicationUrl, '_blank'); }}
            >
              Apply <ExternalLink className="h-3 w-3 ml-1" />
            </Button>
          )}
          {/* Save — adds this grant to the Saved Grants list */}
          <Button
            size="sm"
            variant={isSaved ? "outline" : "default"}
            className={isSaved
              ? "border-teal-600 text-teal-600 hover:bg-teal-50 text-[11px] px-2 py-1 h-auto"
              : "bg-teal-600 hover:bg-teal-700 text-white text-[11px] px-2 py-1 h-auto"}
            onClick={(e) => { e.stopPropagation(); onSave?.(); }}
          >
            {isSaved ? <><BookmarkCheck className="h-3 w-3 mr-1" />Saved</> : <><Bookmark className="h-3 w-3 mr-1" />Save</>}
          </Button>
          <Button
            variant="ghost" size="sm"
            className="text-teal-600 hover:text-teal-700 p-0 h-auto"
            onClick={(e) => { e.stopPropagation(); setShowExplanation(v => !v); }}
          >
            {showExplanation ? "Hide Details" : "View Details"}
            <ChevronRight className={`h-3 w-3 ml-1 transition-transform ${showExplanation ? "rotate-90" : ""}`} />
          </Button>
        </div>
      </div>

      {/* Details Panel — always visible when expanded */}
      {showExplanation && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          className="mt-3 pt-3 border-t border-zinc-100 space-y-2"
          onClick={e => e.stopPropagation()}
        >
          {/* Measure detail — shown when a tile is selected, inside the expanded panel */}
          {selectedMeasure ? (
            <div className="bg-teal-50 border border-teal-200 rounded-lg p-3" onClick={e => e.stopPropagation()}>
              <div className="flex items-start justify-between mb-1">
                <p className="text-xs font-semibold text-teal-900">{measureLabels[selectedMeasure]}</p>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold text-teal-700">{formatMeasureValue(selectedMeasure, grant.measures[selectedMeasure])}</span>
                  <button onClick={e => { e.stopPropagation(); setSelectedMeasure(null); }}><X className="h-3 w-3 text-zinc-400 hover:text-zinc-600" /></button>
                </div>
              </div>
              <p className="text-xs text-zinc-600 leading-relaxed">{measureDescriptions[selectedMeasure]}</p>
              <p className="text-[10px] text-zinc-400 mt-1.5">💡 Tap any other metric tile above to compare.</p>
            </div>
          ) : (
            /* All-metrics summary when no tile selected */
            <div className="rounded-lg border border-zinc-100 divide-y divide-zinc-100" onClick={e => e.stopPropagation()}>
              {Object.entries(grant.measures).map(([key, value]) => {
                const MeasureIcon = measureIcons[key];
                return (
                  <div key={key} className="flex items-center justify-between px-3 py-2 hover:bg-zinc-50 cursor-pointer"
                    onClick={e => { e.stopPropagation(); setSelectedMeasure(key); }}>
                    <div className="flex items-center gap-2">
                      <MeasureIcon className="h-3.5 w-3.5 text-zinc-400 shrink-0" />
                      <div>
                        <p className="text-xs font-medium text-zinc-800">{measureLabels[key]}</p>
                        <p className="text-[10px] text-zinc-400 leading-tight">{measureDescriptions[key]}</p>
                      </div>
                    </div>
                    <span className="text-xs font-bold text-teal-700 ml-3 shrink-0">{formatMeasureValue(key, value)}</span>
                  </div>
                );
              })}
            </div>
          )}
          {/* Hard filter evidence */}
          {rp?.hard_filter_evidence && (
            <div>
              <p className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wide mb-1">Eligibility Checks</p>
              <div className="flex flex-wrap gap-1">
                {Object.entries(rp.hard_filter_evidence).map(([k, v]) => (
                  <span key={k} className={`text-[10px] px-1.5 py-0.5 rounded border ${v.pass ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-600 border-red-200'}`}>
                    {v.pass ? '✓' : '✗'} {k.replace('_',' ')}
                  </span>
                ))}
              </div>
            </div>
          )}
          {/* Semantic alignment */}
          {rp?.semantic_alignment?.top_keywords?.length > 0 && (
            <div>
              <p className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wide mb-1">Keyword Alignment</p>
              <div className="flex flex-wrap gap-1">
                {rp.semantic_alignment.top_keywords.map(kw => (
                  <span key={kw} className="text-[10px] px-1.5 py-0.5 rounded bg-teal-50 text-teal-700 border border-teal-200">{kw}</span>
                ))}
              </div>
            </div>
          )}
          {/* Score breakdown */}
          {rp?.score_breakdown && (
            <div className="flex flex-wrap gap-3 text-[10px] text-zinc-500">
              <span>Semantic: {((rp.score_breakdown.tier2_score || 0) * 100).toFixed(0)}%</span>
              <span>Structural: {((rp.score_breakdown.tier3_score || 0) * 100).toFixed(0)}%</span>
              <span>Final: {((rp.score_breakdown.final_score || 0) * 100).toFixed(0)}%</span>
              <ConfidenceBadge confidence={rp.confidence} />
            </div>
          )}
          {/* GraphRAG rationale */}
          {rationale?.match_rationale && (
            <div>
              <p className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wide mb-1">AI Match Rationale</p>
              <p className="text-xs text-zinc-700 leading-relaxed">{rationale.match_rationale}</p>
            </div>
          )}
          {rationale?.alignment_factors?.length > 0 && (
            <div>
              <p className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wide mb-1">Alignment Factors</p>
              <div className="flex flex-wrap gap-1">
                {rationale.alignment_factors.map((f, i) => (
                  <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-teal-50 text-teal-700 border border-teal-200">{f}</span>
                ))}
              </div>
            </div>
          )}
          {rationale?.risk_flags?.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {rationale.risk_flags.map((f, i) => (
                <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">⚠ {f}</span>
              ))}
            </div>
          )}
          {rationale?.confidence_level && (
            <div className="flex items-center gap-1 text-[10px] text-zinc-500">
              AI confidence: <ConfidenceBadge confidence={rationale.confidence_level} />
            </div>
          )}
          {/* Competition Density — shown when data loads after expand */}
          {grantCompetition && (
            <div className={`flex items-start gap-2 p-2.5 rounded-lg border text-xs ${
              grantCompetition.competition_intensity === 'HIGH'
                ? 'bg-orange-50 border-orange-200 text-orange-800'
                : grantCompetition.competition_intensity === 'MEDIUM'
                ? 'bg-amber-50 border-amber-200 text-amber-800'
                : 'bg-green-50 border-green-200 text-green-800'
            }`}>
              <Swords className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <div>
                <span className="font-semibold">{grantCompetition.competition_intensity} competition</span>
                {' — '}
                {grantCompetition.mirror_plus_direct_competitors > 0
                  ? `${grantCompetition.mirror_plus_direct_competitors} direct or mirror competitors likely eligible for this grant.`
                  : 'No direct competitors detected for this grant.'}
                {grantCompetition.total_eligible_orgs > 0 && (
                  <span className="text-[10px] opacity-75 ml-1">({grantCompetition.total_eligible_orgs} total matched orgs)</span>
                )}
              </div>
            </div>
          )}
          {/* Next Steps — expandable action cards */}
          <div className="border border-zinc-100 rounded-lg overflow-hidden" onClick={e => e.stopPropagation()}>
            <p className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wide px-3 py-2 bg-zinc-50 border-b border-zinc-100">Next Steps</p>
            {[
              {
                icon: FileText,
                color: "text-blue-500",
                label: "Review Funder Guidelines",
                desc: `Read ${grant.funderName}'s program guidelines and funding priorities. Confirm your organization's activities align with their stated mission and geographic focus.`,
                action: grant.applicationUrl ? () => window.open(grant.applicationUrl, '_blank') : null,
                actionLabel: "Open Funder Website →",
              },
              {
                icon: CheckCircle2,
                color: "text-green-500",
                label: "Confirm Your Eligibility",
                desc: "Verify your legal status (charity registration), annual revenue range, project type, and whether you meet any sector-specific criteria the funder requires.",
                action: null,
              },
              {
                icon: Target,
                color: "text-purple-500",
                label: "Strengthen Your Application",
                desc: `Your match score is ${grant.matchScore}%. Improve it by tailoring your project description to mirror the funder's language, showing measurable outcomes, and demonstrating community need.`,
                action: null,
              },
              {
                icon: Clock,
                color: "text-amber-500",
                label: grant.deadline === 'Rolling' ? "Rolling Deadline — Apply Anytime" : `Deadline: ${grant.deadline}`,
                desc: grant.deadline === 'Rolling'
                  ? "This funder accepts applications year-round. Plan to submit at least 3–4 weeks before your program start date."
                  : "Add this deadline to your calendar. Most funders recommend submitting 2–4 weeks early to allow for questions.",
                action: null,
              },
            ].map((step, i) => (
              <ExpandableStep key={i} step={step} />
            ))}
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};

const NearMissCard = ({ alert }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-amber-50 rounded-xl border border-amber-200 p-5 cursor-pointer hover:border-amber-400 hover:shadow-sm transition-all"
      data-testid={`nearmiss-card-${alert.id}`}
      onClick={() => setExpanded(v => !v)}
    >
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <h3 className="font-heading font-semibold text-zinc-900">{alert.programName}</h3>
          </div>
          <p className="text-sm text-zinc-500">{alert.funderName}</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge className="bg-amber-100 text-amber-700">
            {Math.max(0, Math.round((0.60 - (alert.distanceToQualify ?? alert.matchScore / 100 ?? 0)) * 100))}% to qualify
          </Badge>
          <ChevronRight className={`h-4 w-4 text-amber-500 transition-transform ${expanded ? "rotate-90" : ""}`} />
        </div>
      </div>

      {expanded && (
        <>
          {/* Gaps */}
          <div className="space-y-2 mb-4">
            {alert.gaps.map((gap, i) => (
              <div key={i} className="bg-white rounded-lg p-3 border border-amber-100" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-zinc-500 uppercase">{gap.type.replace("_", " ")}</span>
                  <Badge variant="outline" className="text-xs">{gap.effort} effort</Badge>
                </div>
                <p className="text-sm text-zinc-900">
                  <span className="text-red-600">{gap.current}</span> → <span className="text-emerald-600">{gap.required}</span>
                </p>
              </div>
            ))}
          </div>

          {/* Repositioning Steps */}
          <div className="border-t border-amber-200 pt-4">
            <p className="text-xs font-medium text-amber-800 mb-2">Repositioning Suggestions</p>
            <ul className="space-y-1">
              {alert.repositioningSteps.slice(0, 2).map((step, i) => (
                <li key={i} className="text-xs text-zinc-600 flex items-start gap-2">
                  <span className="text-amber-500 mt-0.5">•</span>
                  {step}
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </motion.div>
  );
};

// ── ScoreRow: labelled progress bar for near-miss breakdown ──────────────────
const ScoreRow = ({ label, value, threshold, good, bad }) => {
  const passing = value >= threshold;
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[11px] font-medium text-zinc-700">{label}</span>
        <span className={`text-[11px] font-bold ${passing ? 'text-green-600' : 'text-amber-600'}`}>
          {value}% {passing ? '✓' : '⚠'}
        </span>
      </div>
      <div className="h-2 bg-zinc-200 rounded-full overflow-hidden mb-1">
        <div
          className={`h-full rounded-full transition-all ${passing ? 'bg-green-400' : 'bg-amber-400'}`}
          style={{ width: `${Math.min(100, value)}%` }}
        />
      </div>
      <p className="text-[10px] text-zinc-500 italic">{passing ? good : bad}</p>
    </div>
  );
};

const RealNearMissCard = ({ item }) => {
  const [expanded, setExpanded] = useState(false);
  const rp = item.retrieval_path;
  const scorePercent = Math.round((item.final_score || 0) * 100);
  const threshold = 60;
  const gapPercent = Math.max(0, threshold - scorePercent);
  const barFill = Math.min(100, (scorePercent / threshold) * 100);

  return (
    <div
      className="bg-white rounded-xl border border-amber-200 p-4 cursor-pointer hover:border-amber-400 hover:shadow-sm transition-all"
      onClick={() => setExpanded(v => !v)}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-zinc-900">{item.program_name}</p>
          <p className="text-xs text-zinc-500 mt-1">{item.provider_name}</p>
        </div>
        <div className="flex items-center gap-2 ml-2">
          <Badge className="bg-amber-100 text-amber-700 text-xs">
            {scorePercent}% match
          </Badge>
          <ChevronRight className={`h-4 w-4 text-amber-500 transition-transform shrink-0 ${expanded ? "rotate-90" : ""}`} />
        </div>
      </div>
      {expanded && (
        <div className="mt-3 pt-3 border-t border-amber-100 space-y-3" onClick={e => e.stopPropagation()}>

          {/* Overall proximity to threshold */}
          <div className="bg-amber-50 rounded-lg p-2.5 border border-amber-200">
            <div className="flex items-center justify-between mb-1.5">
              <p className="text-[11px] font-medium text-amber-800">
                {gapPercent > 0
                  ? `${gapPercent}% below qualifying threshold`
                  : "At or above threshold — borderline qualifier"}
              </p>
              <span className="text-[10px] font-bold text-amber-700">{scorePercent}% / {threshold}%</span>
            </div>
            <div className="h-2 bg-amber-200 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${gapPercent > 0 ? 'bg-amber-400' : 'bg-green-400'}`}
                style={{ width: `${barFill}%` }}
              />
            </div>
          </div>

          {/* Score bars with labels */}
          <div>
            <p className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wide mb-2">Score Breakdown</p>
            <div className="space-y-2">
              {/* Mission Alignment (tier2) */}
              {rp?.score_breakdown && (() => {
                const t2 = Math.round((rp.score_breakdown.tier2_score || 0) * 100);
                const t3 = Math.round((rp.score_breakdown.tier3_score || 0) * 100);
                return (
                  <>
                    <ScoreRow
                      label="Mission Alignment"
                      value={t2}
                      threshold={60}
                      good="Your mission text aligns well with this funder's focus areas"
                      bad="Your mission description needs stronger alignment with this funder's priorities"
                    />
                    <ScoreRow
                      label="Eligibility Match"
                      value={t3}
                      threshold={60}
                      good="Your organization profile fits this funder's eligibility criteria"
                      bad="Possible gaps in geographic focus, organization type, or financial profile"
                    />
                  </>
                );
              })()}
              {!rp?.score_breakdown && (
                <div className="text-[11px] text-zinc-400 italic">Detailed score breakdown not available for this funder.</div>
              )}
            </div>
          </div>

          {/* Gap analysis — what's holding you back */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5">
            <p className="text-[10px] font-semibold text-amber-800 mb-1.5">What's holding you back</p>
            <div className="space-y-1.5 text-[11px]">
              {rp?.score_breakdown && (rp.score_breakdown.tier2_score || 0) < 0.60 && (
                <div className="flex items-start gap-1.5">
                  <span className="text-amber-500 shrink-0 mt-0.5">⚠</span>
                  <p className="text-zinc-700">
                    <span className="font-medium text-amber-700">Weak mission match ({Math.round((rp.score_breakdown.tier2_score||0)*100)}%): </span>
                    Your mission statement doesn't strongly reflect this funder's focus. Revising it to use language closer to their stated priorities can lift this score.
                  </p>
                </div>
              )}
              {rp?.score_breakdown && (rp.score_breakdown.tier3_score || 0) < 0.60 && (
                <div className="flex items-start gap-1.5">
                  <span className="text-amber-500 shrink-0 mt-0.5">⚠</span>
                  <p className="text-zinc-700">
                    <span className="font-medium text-amber-700">Low eligibility score ({Math.round((rp.score_breakdown.tier3_score||0)*100)}%): </span>
                    This typically reflects a mismatch in one or more of: <strong>geographic area</strong> (province/region), <strong>organization type</strong> (charity vs foundation vs CBO), or <strong>revenue range</strong>.
                  </p>
                </div>
              )}
              {rp?.confidence === 'VECTOR_ONLY' && (
                <div className="flex items-start gap-1.5 pt-1 border-t border-amber-100">
                  <span className="text-zinc-400 shrink-0 mt-0.5">ℹ</span>
                  <p className="text-zinc-500 italic">
                    Limited funder data available — we matched on mission similarity only. Full eligibility criteria for this funder are not yet in our database.
                  </p>
                </div>
              )}
              {!rp?.score_breakdown && (
                <p className="text-zinc-500">Score details unavailable. Expand your profile and re-run matching for a deeper analysis.</p>
              )}
            </div>
          </div>

          {/* How to close the gap */}
          <div>
            <p className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wide mb-1.5">How to close the gap</p>
            <div className="space-y-1 text-[11px] text-zinc-600">
              {rp?.score_breakdown && (rp.score_breakdown.tier2_score || 0) < 0.60 && (
                <div className="flex items-start gap-1.5">
                  <span className="text-teal-500 shrink-0">→</span>
                  <span>Rewrite your mission statement to include keywords from this funder's focus areas</span>
                </div>
              )}
              {rp?.score_breakdown && (rp.score_breakdown.tier3_score || 0) < 0.60 && (
                <>
                  <div className="flex items-start gap-1.5">
                    <span className="text-teal-500 shrink-0">→</span>
                    <span>Complete your organization profile (geographic focus, org type, annual revenue)</span>
                  </div>
                  <div className="flex items-start gap-1.5">
                    <span className="text-teal-500 shrink-0">→</span>
                    <span>Verify you meet the funder's geographic and organizational eligibility requirements</span>
                  </div>
                </>
              )}
              <div className="flex items-start gap-1.5">
                <span className="text-teal-500 shrink-0">→</span>
                <span>Review the funder's website for their complete eligibility criteria</span>
              </div>
            </div>
          </div>

          {/* Apply link */}
          {item.application_url && (
            <Button
              variant="ghost" size="sm"
              className="text-teal-600 hover:text-teal-700 p-0 h-auto text-xs border border-teal-200 px-2 py-1 rounded"
              onClick={e => { e.stopPropagation(); window.open(item.application_url, '_blank'); }}
            >
              Visit Funder Website <ExternalLink className="h-3 w-3 ml-1" />
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Competition Stage config (mirrors backend) ───────────────────────────────
const STAGE_COLORS = {
  MIRROR:     { bg: "bg-red-50",    border: "border-red-200",    text: "text-red-700",    badge: "bg-red-100 text-red-700 border-red-200",    dot: "bg-red-500" },
  DIRECT:     { bg: "bg-amber-50",  border: "border-amber-200",  text: "text-amber-700",  badge: "bg-amber-100 text-amber-700 border-amber-200",  dot: "bg-amber-500" },
  METHOD_ALT: { bg: "bg-yellow-50", border: "border-yellow-200", text: "text-yellow-700", badge: "bg-yellow-100 text-yellow-700 border-yellow-200", dot: "bg-yellow-400" },
  INDIRECT:   { bg: "bg-zinc-50",   border: "border-zinc-200",   text: "text-zinc-600",   badge: "bg-zinc-100 text-zinc-600 border-zinc-200",   dot: "bg-zinc-400" },
  ADJACENT:   { bg: "bg-teal-50",   border: "border-teal-100",   text: "text-teal-600",   badge: "bg-teal-100 text-teal-600 border-teal-200",   dot: "bg-teal-400" },
};

const STAGE_LABELS = {
  MIRROR: "Mirror",  DIRECT: "Direct",  METHOD_ALT: "Method Alt",
  INDIRECT: "Indirect",  ADJACENT: "Adjacent",
};

const STAGE_FULL = {
  MIRROR:     "Stage 1 · 80-100% — funder must choose between you",
  DIRECT:     "Stage 2 · 60-79% — funder will ask you to differentiate",
  METHOD_ALT: "Stage 3 · 35-59% — funder can support both; different approach",
  INDIRECT:   "Stage 4 · 10-34% — different programs; low grant competition",
  ADJACENT:   "Stage 5 · <10% — not competing; potential collaborators",
};

const CompetitorRow = ({ c, onPreview, isActive }) => {
  const colors = STAGE_COLORS[c.stage_label] || STAGE_COLORS.ADJACENT;
  return (
    <div
      className={`flex items-center justify-between px-3 py-2.5 rounded-lg cursor-pointer transition-all border ${
        isActive ? `${colors.bg} ${colors.border}` : "border-transparent hover:bg-zinc-50"
      }`}
      onClick={() => onPreview(c.competitor_bn)}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 mb-0.5">
          <span className={`w-2 h-2 rounded-full shrink-0 ${colors.dot}`} />
          <p className="text-sm font-medium text-zinc-900 truncate">{c.competitor_name}</p>
        </div>
        <p className="text-xs text-zinc-500 pl-3.5">{c.competitor_city}, {c.competitor_province}</p>
      </div>
      <div className="flex items-center gap-2 shrink-0 ml-2">
        <Badge className={`text-[10px] px-1.5 py-0.5 border ${colors.badge}`}>
          {Math.round(c.stage_pct)}%
        </Badge>
        {isActive && <Badge className="bg-teal-100 text-teal-700 border-teal-200 text-[10px]">Viewing ✓</Badge>}
      </div>
    </div>
  );
};

const CompetitorPanel = ({ summary, direct, loading, activeBN, onPreview, previewBN }) => {
  const [stageFilter, setStageFilter] = useState("all"); // all | mirror | direct
  const [showAll, setShowAll] = useState(false);

  const counts = summary?.counts || {};
  const intensity = summary?.competition_intensity;
  const intensityColor = {
    "VERY HIGH": "text-red-600", HIGH: "text-amber-600", MEDIUM: "text-yellow-600", LOW: "text-teal-600",
  }[intensity] || "text-zinc-500";

  const allDirect = [
    ...(direct?.mirror || []).map(c => ({ ...c, _bucket: "mirror" })),
    ...(direct?.direct || []).map(c => ({ ...c, _bucket: "direct" })),
  ];
  const filtered = stageFilter === "mirror" ? allDirect.filter(c => c._bucket === "mirror")
                 : stageFilter === "direct" ? allDirect.filter(c => c._bucket === "direct")
                 : allDirect;
  const visible = showAll ? filtered : filtered.slice(0, 6);

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Swords className="h-4 w-4 text-zinc-500" />
            <CardTitle className="text-base">Competitor Intelligence</CardTitle>
          </div>
          {intensity && (
            <span className={`text-xs font-semibold ${intensityColor}`}>{intensity} competition</span>
          )}
        </div>
        {summary && (
          <div className="flex flex-wrap gap-2 mt-2">
            {[
              { key: "mirror", label: "Mirror", stage: "MIRROR" },
              { key: "direct", label: "Direct", stage: "DIRECT" },
              { key: "method_alt", label: "Method Alt", stage: "METHOD_ALT" },
              { key: "indirect", label: "Indirect", stage: "INDIRECT" },
            ].map(({ key, label, stage }) => {
              const c = STAGE_COLORS[stage];
              const n = counts[key] || 0;
              return (
                <TooltipProvider key={key}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className={`text-xs px-2 py-0.5 rounded-full border font-medium cursor-default ${c.badge}`}>
                        {n} {label}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="text-xs max-w-xs">
                      {STAGE_FULL[stage]}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              );
            })}
          </div>
        )}
      </CardHeader>
      <CardContent className="pt-0">
        {/* Stage filter tabs */}
        <div className="flex gap-1 mb-3">
          {[["all", "All"], ["mirror", "Mirror (S1)"], ["direct", "Direct (S2)"]].map(([v, l]) => (
            <button
              key={v}
              onClick={() => setStageFilter(v)}
              className={`text-xs px-2.5 py-1 rounded-md transition-colors ${
                stageFilter === v
                  ? "bg-zinc-900 text-white"
                  : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"
              }`}
            >
              {l}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-zinc-400 py-4">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm">Analysing competitors…</span>
          </div>
        ) : visible.length === 0 ? (
          <p className="text-sm text-zinc-400 py-3">
            {stageFilter === "mirror"
              ? "No mirror competitors found — strong differentiation."
              : "No direct competitors in this region."}
          </p>
        ) : (
          <div className="space-y-1">
            {visible.map(c => (
              <CompetitorRow
                key={c.competitor_bn}
                c={c}
                onPreview={onPreview}
                isActive={previewBN === c.competitor_bn}
              />
            ))}
            {filtered.length > 6 && (
              <button
                onClick={() => setShowAll(v => !v)}
                className="text-xs text-teal-600 hover:text-teal-700 pt-1 pl-3"
              >
                {showAll ? "Show less" : `Show ${filtered.length - 6} more`}
              </button>
            )}
          </div>
        )}

        {!loading && allDirect.length > 0 && (
          <p className="text-[10px] text-zinc-400 mt-3 border-t pt-2">
            Click a competitor to preview their grant matches. Stage 1 (Mirror) = funder must choose.
            Stage 2 (Direct) = funder will ask you to differentiate.
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export const FundMatchTab = ({ charityBN, onNavigate }) => {
  const [selectedProject, setSelectedProject] = useState(null);
  const [showAllGrants, setShowAllGrants] = useState(false);
  const [previewBN, setPreviewBN] = useState(null);
  const activeBN = previewBN || charityBN;

  /* ── Saved Grants ── */
  const SAVED_KEY = "impactplexus_saved_grants";
  const [savedGrants, setSavedGrants] = useState(() => {
    try {
      const all = JSON.parse(localStorage.getItem(SAVED_KEY) || "{}");
      return all[charityBN] || [];
    } catch { return []; }
  });

  const toggleSave = (grant) => {
    setSavedGrants(prev => {
      const exists = prev.find(g => g.grant_id === grant.id);
      const next = exists
        ? prev.filter(g => g.grant_id !== grant.id)
        : [...prev, {
            grant_id: grant.id,
            program_name: grant.programName,
            provider_name: grant.funderName,
            median_gift: grant.amount,
            next_deadline: grant.deadline,
            final_score: (grant.matchScore || 0) / 100,
            application_url: grant.applicationUrl,
            saved_at: new Date().toISOString(),
          }];
      try {
        const all = JSON.parse(localStorage.getItem(SAVED_KEY) || "{}");
        all[charityBN] = next;
        localStorage.setItem(SAVED_KEY, JSON.stringify(all));
      } catch {}
      return next;
    });
  };

  const workOn = (savedGrant) => {
    try {
      localStorage.setItem("impactplexus_pending_pursuit", JSON.stringify({
        ...savedGrant,
        phase_hint: "saved",
      }));
    } catch {}
    onNavigate?.("fundreach");
  };
  const projectId = selectedProject?.project_id;

  // useSearchGrants disabled — only fire when user types a search query (no auto-browse)
  const { data: charitiesData } = useCharities({ limit: 10, province: 'ON' });
  // Competition data — loaded when we have a BN
  const { data: competitionSummary } = useCompetitionSummary(activeBN, !!activeBN);
  const { data: directCompetitors, isLoading: competitorsLoading } = useDirectCompetitors(activeBN, 20, !!activeBN);
  // Pass project description to activate v8 HyDE + GraphRAG when a project is selected
  const projectDesc = selectedProject?.project_description || selectedProject?.description || null;
  const { data: matchesData, isLoading: matchesLoading } = useGrantMatches(activeBN, projectDesc);
  const { data: measuresData, isLoading: measuresLoading } = useCharityMeasures(activeBN);
  const { data: tierAData, isLoading: tierALoading } = useDefaultSearchMatches(activeBN);
  const tierAMatches = tierAData?.tier_a?.structured_matches || [];

  // Project-level data (v3) — used when a project is selected
  const { data: projMatchData, isLoading: projMatchLoading } = useProjectMatches(projectId);
  const { data: projMeasuresData, isLoading: projMeasuresLoading } = useProjectMeasures(projectId);
  const discoverMutation = useDiscoverProject(activeBN);

  // Resolve which data source to use
  const usingProject = !!projectId;

  const realMatches = usingProject
    ? (projMatchData?.matches || [])
    : (matchesData?.matches || []).map(m => ({
        ...m,
        // Org-level matches use match_score; project matches use final_score
        final_score: m.final_score ?? m.match_score ?? 0,
        program_name: m.program_name ?? m.grant_name ?? 'Grant Program',
        provider_name: m.provider_name ?? m.funder_name ?? 'Funder',
        median_gift: m.median_gift ?? null,
        next_deadline: m.next_deadline ?? null,
        application_url: m.application_url ?? m.website ?? null,
      }));

  // Qualified: backend already filters to >= 0.60 for org-level matches.
  // For project matches we keep local filtering at 0.60 to match backend threshold.
  const qualifiedGrants = realMatches.filter((m) =>
    usingProject ? (m.final_score >= 0.60) : (m.final_score >= 0.60)
  );
  const nearMissGrants = usingProject
    ? (projMatchData?.near_miss || [])
    : matchesData?.near_miss?.length > 0
      ? matchesData.near_miss.map(m => ({
          ...m,
          final_score: m.final_score ?? m.match_score ?? 0,
          program_name: m.program_name ?? m.grant_name ?? 'Grant Program',
          provider_name: m.provider_name ?? m.funder_name ?? 'Funder',
          median_gift: m.median_gift ?? null,
          next_deadline: m.next_deadline ?? null,
          application_url: m.application_url ?? m.website ?? null,
        }))
      : NEAR_MISS_ALERTS;

  // Measures
  const measures = usingProject ? projMeasuresData : measuresData?.measures;
  const totalOpportunities = (usingProject ? measures?.m1_opportunities : measures?.m1_opportunities) ??
    qualifiedGrants.reduce((sum, g) => sum + (g.final_score * 100000), 0);
  const totalExpectations = (usingProject ? measures?.m2_expectations : measures?.m2_expectations) ??
    qualifiedGrants.reduce((sum, g) => sum + (g.final_score * 50000), 0);
  const avgMatchScore = qualifiedGrants.length > 0
    ? (qualifiedGrants.reduce((sum, g) => sum + (g.final_score * 100), 0) / qualifiedGrants.length)
    : 0;

  const isLoadingAny = usingProject ? (projMatchLoading || projMeasuresLoading) : (matchesLoading || measuresLoading);
  const hasProjectRun = usingProject && projMatchData?.run_id;
  const noProjectRun = usingProject && !projMatchLoading && !projMatchData?.run_id;

  return (
    <div className="space-y-6" data-testid="fundmatch-tab">
      {/* Projects Panel — v3 project-centric architecture */}
      <ProjectsPanel
        charityBN={activeBN}
        selectedProject={selectedProject}
        onProjectSelect={setSelectedProject}
      />

      {/* Preview banner when browsing another org */}
      {previewBN && (
        <Alert className="border-amber-200 bg-amber-50">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertTitle className="text-amber-900">Previewing another organization</AlertTitle>
          <AlertDescription className="flex items-center justify-between text-amber-800">
            <span>Viewing grants for BN: <strong>{previewBN}</strong></span>
            <Button size="sm" variant="outline" className="ml-4 shrink-0" onClick={() => setPreviewBN(null)}>
              Return to my account
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Run Match CTA — shown when project selected but no run yet */}
      {noProjectRun && (
        <Alert className="border-teal-200 bg-teal-50">
          <Play className="h-4 w-4 text-teal-600" />
          <AlertTitle className="text-teal-900">Run Grant Discovery for this Project</AlertTitle>
          <AlertDescription className="text-teal-800 flex items-center justify-between">
            <span>
              Discover grants matched to <strong>{selectedProject.project_name}</strong>'s
              goals, budget ask, and project type.
            </span>
            <Button
              size="sm"
              onClick={() => discoverMutation.mutate(projectId)}
              disabled={discoverMutation.isPending}
              className="ml-4 bg-teal-600 hover:bg-teal-700 text-white shrink-0"
            >
              {discoverMutation.isPending ? (
                <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Running…</>
              ) : (
                <><Play className="h-3 w-3 mr-1" /> Run Match</>
              )}
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Project match run status */}
      {hasProjectRun && (
        <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg px-4 py-2">
          <div className="flex items-center gap-2 text-sm text-green-800">
            <CheckCircle2 className="h-4 w-4 text-green-500" />
            <span>
              Last run: {projMatchData.run_date ? new Date(projMatchData.run_date).toLocaleString() : "recently"} ·
              {" "}{projMatchData.total_matched} qualified grants
              {projMatchData.is_stale && <Badge className="ml-2 bg-amber-100 text-amber-700 text-xs">Stale — re-run recommended</Badge>}
            </span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => discoverMutation.mutate(projectId)}
            disabled={discoverMutation.isPending}
            className="text-teal-600 hover:text-teal-700 text-xs"
          >
            {discoverMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : "Re-run"}
          </Button>
        </div>
      )}

      {/* API Status — ultra-compact single-line */}
      <div className="flex items-center gap-2 bg-blue-50/60 border border-blue-100 rounded px-3 py-1 text-[10px] text-blue-700 leading-none">
        <Database className="h-3 w-3 text-blue-500 shrink-0" />
        {matchesLoading && activeBN ? (
          <span className="flex items-center gap-1.5"><Loader2 className="h-2.5 w-2.5 animate-spin" />Connecting…</span>
        ) : (
          <span className="flex items-center gap-2 overflow-hidden">
            <span className="font-medium">✅ Live</span>
            {charitiesData && <span className="hidden sm:inline">· {charitiesData.pagination?.returned || 0} charities</span>}
            {activeBN && qualifiedGrants.length > 0 && <span>· {qualifiedGrants.length} grants matched</span>}
          </span>
        )}
      </div>

      {/* Summary Stats — v5: data_confidence badges */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <MeasureCard
          label="M1 · Total Opportunities"
          value={`$${totalOpportunities > 1_000_000 ? `${(totalOpportunities/1_000_000).toFixed(2)}M` : `${(totalOpportunities/1000).toFixed(0)}K`}`}
          icon={<DollarSign className="h-6 w-6 text-teal-600" />}
          iconBg="from-slate-100 to-teal-100"
          confidence={measures?.data_confidence?.m1_opportunities}
        />
        <MeasureCard
          label="M2 · Expected Value"
          value={`$${totalExpectations > 1_000_000 ? `${(totalExpectations/1_000_000).toFixed(2)}M` : `${(totalExpectations/1000).toFixed(0)}K`}`}
          icon={<TrendingUp className="h-6 w-6 text-cyan-600" />}
          iconBg="bg-cyan-100"
          confidence={measures?.data_confidence?.m2_expectations}
        />
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-zinc-500 mb-1">Qualified Grants</p>
                <p className="text-2xl font-heading font-bold text-zinc-900">{qualifiedGrants.length}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                <Target className="h-6 w-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-zinc-500 mb-1">Avg Match Score</p>
                <p className="text-2xl font-heading font-bold text-zinc-900">{avgMatchScore.toFixed(0)}%</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-violet-100 flex items-center justify-center">
                <BarChart3 className="h-6 w-6 text-violet-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tier A: Province-Matched Funders (v9.4) */}
      {activeBN && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-heading font-semibold text-zinc-900 flex items-center gap-2">
              <MapPin className="h-4 w-4 text-teal-600" />
              Province-Matched Funders
            </h2>
            {competitionSummary && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="flex items-center gap-1 text-xs text-amber-600 font-medium cursor-default">
                      <Users className="h-3.5 w-3.5" />
                      {(competitionSummary.counts?.mirror || 0) + (competitionSummary.counts?.direct || 0)} competitors in region
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="text-xs max-w-xs">
                    {competitionSummary.counts?.mirror || 0} Mirror + {competitionSummary.counts?.direct || 0} Direct competitors
                    are also eligible for grants in your province
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            <span className="text-xs text-zinc-400 flex items-center gap-1">
              <Database className="h-3 w-3" />
              geo-filtered · v9.4
            </span>
          </div>
          {tierALoading ? (
            <div className="flex items-center gap-2 text-zinc-500 py-4">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm">Loading province-matched funders…</span>
            </div>
          ) : tierAMatches.length > 0 ? (
            <Card>
              <CardContent className="p-0 divide-y divide-zinc-100">
                {tierAMatches.map((m, idx) => {
                  const geoColors = {
                    REGIONAL:   'bg-green-100 text-green-700',
                    PROVINCIAL: 'bg-teal-100 text-teal-700',
                    NATIONAL:   'bg-blue-100 text-blue-700',
                    LOCAL:      'bg-emerald-100 text-emerald-700',
                    UNKNOWN:    'bg-zinc-100 text-zinc-500',
                    RESTRICTED: 'bg-orange-100 text-orange-700',
                  };
                  const geoBadge = geoColors[m.geo_type] || geoColors.UNKNOWN;
                  return (
                    <div key={m.program_id || idx} className="flex items-center justify-between p-3 hover:bg-zinc-50 transition-colors">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-zinc-900 truncate">{m.program_name || m.funder_name}</p>
                        <p className="text-xs text-zinc-500 truncate">{m.funder_name}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${geoBadge}`}>{m.geo_type}</span>
                          {m.median_gift && <span className="text-xs text-zinc-400">{m.median_gift}</span>}
                          {m.next_deadline && (
                            <span className="text-xs text-zinc-400 flex items-center gap-0.5">
                              <Calendar className="h-3 w-3" />
                              {new Date(m.next_deadline).toLocaleDateString("en-CA", { month: "short", day: "numeric" })}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0 ml-2">
                        <span className="text-sm font-bold text-teal-700">{Math.round(m.tier_a_score * 100)}%</span>
                        {m.website && (
                          <Button
                            variant="ghost" size="sm"
                            className="text-zinc-400 hover:text-teal-600 p-1 h-auto"
                            onClick={() => window.open(m.website, '_blank')}
                          >
                            <ExternalLink className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          ) : (
            <p className="text-sm text-zinc-400 py-2">No province-matched funders found.</p>
          )}
        </div>
      )}

      {/* Main Content Grid */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Grant Opportunities */}
        <div className="lg:col-span-2 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-heading font-semibold text-zinc-900">
              {usingProject
                ? `Grants for: ${selectedProject.project_name}`
                : 'Top Grant Opportunities'}
            </h2>
            <Button variant="ghost" size="sm" onClick={() => setShowAllGrants(v => !v)}>
              {showAllGrants ? "Show Less" : "View All"}
              {showAllGrants ? <ChevronUp className="h-3 w-3 ml-1" /> : <ChevronDown className="h-3 w-3 ml-1" />}
            </Button>
          </div>
          <div className="space-y-4">
            {(usingProject ? projMatchLoading : matchesLoading) && activeBN ? (
              <div className="flex items-center gap-2 text-zinc-500 py-8">
                <Loader2 className="h-5 w-5 animate-spin" />
                <span>{usingProject ? `Discovering grants for ${selectedProject.project_name}…` : 'Loading your matched grants…'}</span>
              </div>
            ) : qualifiedGrants.length > 0 ? (
              qualifiedGrants.slice(0, showAllGrants ? undefined : 4).map((match, idx) => (
                <GrantCard
                  key={match.grant_id || idx}
                  grant={{
                    id: match.grant_id,
                    programName: match.program_name || 'Grant Program',
                    funderName: match.provider_name || 'Funder',
                    matchScore: Math.round((match.final_score || 0) * 100),
                    amount: match.median_gift || 'Varies',
                    deadline: match.next_deadline || 'Rolling',
                    applicationUrl: match.application_url || null,
                    status: 'qualified',
                    source: match.source || null,
                    tags: [usingProject ? 'Project-Matched' : (match.embedding_model ? 'AI-Matched' : 'Matched')],
                    nextSteps: ['Review program guidelines', 'Prepare application'],
                    retrieval_path: match.retrieval_path || null,
                    rationale: match.rationale || null,
                    measures: {
                      opportunities: measures?.m1_opportunities ?? null,
                      expectations: measures?.m2_expectations ?? measures?.m2_expected_value ?? null,
                      leverage: measures?.m5_leverage ?? null,
                      positioning: measures?.m6_positioning ?? null,
                    },
                  }}
                  onNavigate={onNavigate}
                  onSave={() => toggleSave({ id: match.grant_id, programName: match.program_name || 'Grant Program', funderName: match.provider_name || 'Funder', amount: match.median_gift, deadline: match.next_deadline, matchScore: Math.round((match.final_score || 0) * 100), applicationUrl: match.application_url })}
                  isSaved={savedGrants.some(g => g.grant_id === match.grant_id)}
                  orgBN={activeBN}
                />
              ))
            ) : (
              <Card className="border-dashed">
                <CardContent className="p-8 text-center space-y-3">
                  <p className="text-zinc-500 font-medium">No highly-qualified grants found yet</p>
                  {usingProject ? (
                    <p className="text-zinc-400 text-sm">Click "Run Match" above to discover grants tailored to this project.</p>
                  ) : activeBN ? (
                    <>
                      <p className="text-zinc-400 text-sm">Check Near-Miss Alerts below — grants close to qualifying are shown there.</p>
                      <p className="text-zinc-400 text-xs">Adding a project description activates AI-powered matching for better results.</p>
                    </>
                  ) : (
                    <p className="text-zinc-400 text-sm">Complete onboarding to see your personalized grant matches.</p>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* ── Right Sidebar ─────────────────────────────────────────────── */}
        <div className="space-y-4">

          {/* Competition Landscape Sidebar Widget */}
          {activeBN && (
            <Card className="border border-zinc-200">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm flex items-center gap-1.5">
                    <Swords className="h-3.5 w-3.5 text-orange-500" />
                    Competitor Density
                  </CardTitle>
                  {competitionSummary?.competition_intensity && (
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${
                      competitionSummary.competition_intensity === 'VERY HIGH' ? 'bg-red-100 text-red-700 border-red-200' :
                      competitionSummary.competition_intensity === 'HIGH'      ? 'bg-amber-100 text-amber-700 border-amber-200' :
                      competitionSummary.competition_intensity === 'MEDIUM'    ? 'bg-yellow-100 text-yellow-700 border-yellow-200' :
                                                                                 'bg-green-100 text-green-700 border-green-200'
                    }`}>{competitionSummary.competition_intensity}</span>
                  )}
                </div>
              </CardHeader>
              <CardContent className="pt-0 space-y-3">
                {competitionSummary ? (
                  <>
                    {/* Stage bar chart */}
                    {[
                      { key: 'mirror',     label: 'Mirror',  color: 'bg-red-500',    max: 50 },
                      { key: 'direct',     label: 'Direct',  color: 'bg-amber-500',  max: 50 },
                      { key: 'method_alt', label: 'Meth Alt',color: 'bg-yellow-400', max: 200 },
                      { key: 'indirect',   label: 'Indirect',color: 'bg-zinc-400',   max: 200 },
                    ].map(({ key, label, color, max }) => {
                      const n = competitionSummary.counts?.[key] || 0;
                      const pct = Math.min(100, (n / max) * 100);
                      return (
                        <div key={key}>
                          <div className="flex justify-between text-[10px] text-zinc-500 mb-0.5">
                            <span>{label}</span>
                            <span className="font-semibold text-zinc-700">{n}</span>
                          </div>
                          <div className="h-1.5 bg-zinc-100 rounded-full overflow-hidden">
                            <div className={`h-full ${color} rounded-full transition-all`} style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      );
                    })}

                    {/* Top mirror/direct competitors */}
                    {directCompetitors && (
                      <div className="pt-1">
                        <p className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wide mb-1.5">Top Competitors</p>
                        <div className="space-y-1.5">
                          {[...(directCompetitors.mirror || []).map(c => ({ ...c, _s: 'MIRROR' })),
                            ...(directCompetitors.direct || []).map(c => ({ ...c, _s: 'DIRECT' }))]
                            .slice(0, 4)
                            .map((c, i) => (
                              <div key={i} className="flex items-center gap-1.5">
                                <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${c._s === 'MIRROR' ? 'bg-red-500' : 'bg-amber-500'}`} />
                                <p className="text-[11px] text-zinc-700 truncate flex-1">{c.competitor_name}</p>
                                <span className="text-[10px] font-bold text-zinc-500 shrink-0">{Math.round(c.stage_pct)}%</span>
                              </div>
                            ))}
                        </div>
                      </div>
                    )}

                    {competitorsLoading && (
                      <div className="flex items-center gap-1.5 text-zinc-400 text-[11px]">
                        <Loader2 className="h-3 w-3 animate-spin" />Loading…
                      </div>
                    )}
                    <p className="text-[10px] text-zinc-400 pt-1 border-t">
                      {(competitionSummary.counts?.mirror || 0) + (competitionSummary.counts?.direct || 0)} direct/mirror competitors in your province
                    </p>
                  </>
                ) : (
                  <div className="flex items-center gap-1.5 text-zinc-400 text-xs py-2">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    <span>Analysing…</span>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Saved Grants Repository */}
          {savedGrants.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-heading font-semibold text-zinc-900 flex items-center gap-2">
                <BookmarkCheck className="h-4 w-4 text-teal-600" />
                Saved Grants
                <Badge className="bg-teal-100 text-teal-800 text-xs">{savedGrants.length}</Badge>
              </h2>
            </div>
            <Card className="border border-teal-100">
              <CardContent className="p-0 divide-y divide-zinc-100">
                {savedGrants.map((sg, i) => (
                  <div key={sg.grant_id || i} className="p-3 flex items-center justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-zinc-900 truncate">{sg.program_name || sg.provider_name}</p>
                      <p className="text-xs text-zinc-500 truncate">{sg.provider_name}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs font-bold text-teal-700">{Math.round((sg.final_score || 0) * 100)}% match</span>
                        {sg.median_gift && <span className="text-xs text-zinc-400">{sg.median_gift}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <Button
                        size="sm"
                        className="bg-teal-600 hover:bg-teal-700 text-white text-[11px] px-2 py-1 h-auto"
                        onClick={() => workOn(sg)}
                      >
                        Work On →
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-zinc-400 hover:text-red-500 p-1 h-auto"
                        onClick={() => toggleSave({ id: sg.grant_id, programName: sg.program_name, funderName: sg.provider_name, amount: sg.median_gift, deadline: sg.next_deadline, matchScore: Math.round((sg.final_score || 0) * 100), applicationUrl: sg.application_url })}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
            <p className="text-xs text-zinc-400 px-1">Click "Work On →" to activate a grant in FundReach for processing.</p>
          </div>
        )}

        {/* Near-Miss Alerts */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-heading font-semibold text-zinc-900">Near-Miss Alerts</h2>
            <Badge className="bg-amber-100 text-amber-700">{nearMissGrants.length}</Badge>
          </div>
          <div className="space-y-4">
            {nearMissGrants.slice(0, 5).map((item, idx) => (
              item.id && !item.grant_id
                ? <NearMissCard key={item.id} alert={item} />
                : <RealNearMissCard key={item.grant_id || idx} item={item} />
            ))}
          </div>
        </div>
        {/* ── End Right Sidebar ─────────────────────────────────────────── */}
        </div>
      </div>

      {/* Competitor Intelligence Panel — replaces generic Ontario Charities list */}
      {activeBN && (
        <CompetitorPanel
          summary={competitionSummary}
          direct={directCompetitors}
          loading={competitorsLoading}
          activeBN={activeBN}
          onPreview={(bn) => setPreviewBN(prev => prev === bn ? null : bn)}
          previewBN={previewBN}
        />
      )}

      {/* Ontario Charities (Live Data) — shown only when no active BN to browse sample orgs */}
      {!activeBN && charitiesData && charitiesData.charities && charitiesData.charities.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Ontario Charities (Live Data)</CardTitle>
            <CardDescription>Click a charity to preview its grant matches</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {charitiesData.charities.slice(0, 5).map((charity, idx) => {
                const bn = charity["BN/Registration Number"];
                const isActive = activeBN === bn;
                return (
                  <div
                    key={idx}
                    className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-all ${
                      isActive
                        ? 'bg-teal-50 border border-teal-200 shadow-sm'
                        : 'bg-zinc-50 hover:bg-zinc-100 border border-transparent'
                    }`}
                    onClick={() => setPreviewBN(isActive ? null : bn)}
                  >
                    <div>
                      <p className="font-medium text-sm text-zinc-900">{charity["Legal name"]}</p>
                      <p className="text-xs text-zinc-500">
                        {charity.City}, {charity.Province} • BN: {bn}
                      </p>
                    </div>
                    {isActive
                      ? <Badge className="bg-teal-100 text-teal-700 border-teal-200">Viewing ✓</Badge>
                      : <Badge variant="outline">{charity.designation}</Badge>
                    }
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default FundMatchTab;
